import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { setAuthToken } from '@/api/client';
import { rbacApi } from '@/api/rbac';
import { usersApi } from '@/api/users';
import type { LoginResponse, RegisterResponse } from '@/api/auth';

const STORAGE_KEY = 'kp_auth';

interface PersistedAuth {
  userId: string;
  userType: string;
  jwt: string;
  phone?: string;
  displayName?: string;
  mustChangePassword?: boolean;
}

function load(): PersistedAuth | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return null;
  }
}

function save(v: PersistedAuth | null): void {
  if (!v) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = load();
  if (initial) setAuthToken(initial.jwt);

  const userId = ref<string | null>(initial?.userId ?? null);
  const userType = ref<string | null>(initial?.userType ?? null);
  const jwt = ref<string | null>(initial?.jwt ?? null);
  const phone = ref<string | null>(initial?.phone ?? null);
  const displayName = ref<string | null>(initial?.displayName ?? null);
  const mustChangePassword = ref<boolean>(!!initial?.mustChangePassword);
  const role = ref<string | null>(null);
  const permissions = ref<string[]>([]);
  const permsLoaded = ref(false);

  const isLoggedIn = computed(() => !!jwt.value && !!userId.value);
  function canSee(perm: string): boolean { return permissions.value.includes(perm); }

  /** 登录/刷新后拉 /users/me 填 displayName 等 profile 字段 · 顶栏永远显示 displayName */
  async function loadProfile(): Promise<void> {
    if (!isLoggedIn.value) return;
    try {
      const me = await usersApi.me();
      displayName.value = me.displayName;
      if (!phone.value) phone.value = me.phoneE164;
      const raw = load();
      if (raw) save({ ...raw, displayName: me.displayName, phone: phone.value ?? raw.phone });
    } catch { /* 静默 · 顶栏会 fallback 到 phone */ }
  }

  async function loadPermissions(): Promise<void> {
    if (!isLoggedIn.value) {
      permissions.value = [];
      role.value = null;
      permsLoaded.value = true;
      return;
    }
    try {
      const r = await rbacApi.myPermissions();
      role.value = r.role;
      permissions.value = r.permissions;
    } catch {
      permissions.value = [];
      role.value = null;
    } finally {
      permsLoaded.value = true;
    }
  }

  function setFromLogin(resp: LoginResponse, phoneUsed: string): void {
    userId.value = resp.userId;
    userType.value = resp.userType;
    jwt.value = resp.jwt;
    phone.value = phoneUsed;
    mustChangePassword.value = !!resp.mustChangePassword;
    setAuthToken(resp.jwt);
    save({
      userId: resp.userId,
      userType: resp.userType,
      jwt: resp.jwt,
      phone: phoneUsed,
      displayName: displayName.value ?? undefined,
      mustChangePassword: !!resp.mustChangePassword,
    });
  }

  function clearMustChangePassword(): void {
    mustChangePassword.value = false;
    const raw = load();
    if (raw) save({ ...raw, mustChangePassword: false });
  }

  function setFromRegister(resp: RegisterResponse, phoneUsed: string, displayNameUsed: string): void {
    userId.value = resp.userId;
    userType.value = 'personal';
    jwt.value = resp.jwt;
    phone.value = phoneUsed;
    displayName.value = displayNameUsed;
    setAuthToken(resp.jwt);
    save({
      userId: resp.userId,
      userType: 'personal',
      jwt: resp.jwt,
      phone: phoneUsed,
      displayName: displayNameUsed,
    });
  }

  function logout(): void {
    userId.value = null;
    userType.value = null;
    jwt.value = null;
    phone.value = null;
    displayName.value = null;
    mustChangePassword.value = false;
    role.value = null;
    permissions.value = [];
    permsLoaded.value = false;
    setAuthToken(null);
    save(null);
  }

  return {
    userId,
    userType,
    jwt,
    phone,
    displayName,
    mustChangePassword,
    role,
    permissions,
    permsLoaded,
    isLoggedIn,
    canSee,
    setFromLogin,
    setFromRegister,
    clearMustChangePassword,
    loadPermissions,
    loadProfile,
    logout,
  };
});
